import React from "react";
import { check, PARTNERSHIPS, BUDGET_RANGES } from "../../lib/forms";
import { trackFormSubmit } from "../../lib/track";
import { useSiteForm, SiteForm, Field, Choices, Submit, SendError, ThankYou, RequiredNote } from "./FormKit";

/**
 * Form C — sponsorship and partnerships. The website and the budget are
 * optional; everything else is needed to reply usefully. Posts to the Netlify
 * form "sponsorship" (lib/forms.js).
 */
const RULES = {
  name: [check.required("name")],
  company: [check.required("company or brand")],
  role: [check.required("role")],
  email: [check.email],
  phone: [check.phone()],
  website: [check.url],
  partnership: [check.choose("at least one kind of partnership")],
  cities: [check.required("city or cities")],
  message: [check.required("message — a line or two is plenty")],
};

export default function SponsorshipForm({ tone = "dark" }) {
  const form = useSiteForm("sponsorship", {
    initial: { partnership: [] },
    rules: RULES,
    onSent: (v) => trackFormSubmit("sponsorship", { content_type: v.partnership.join(", ") }),
  });

  if (form.status === "done") {
    return (
      <ThankYou form={form} tone={tone} title="Thank you — it's with us.">
        The Antara team reads every partnership enquiry and will come back to you directly about
        what working together could look like for {form.values.company.trim() || "you"}.
      </ThankYou>
    );
  }

  return (
    <SiteForm form={form} tone={tone} className="max-w-2xl mx-auto space-y-6">
      <RequiredNote />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field form={form} name="name" label="Name" required autoComplete="name" placeholder="Your name" />
        <Field form={form} name="company" label="Company / brand" required autoComplete="organization" placeholder="Brand name" />
        <Field form={form} name="role" label="Role" required autoComplete="organization-title" placeholder="e.g. Brand manager" />
        <Field
          form={form} name="email" label="Work email" required
          type="email" inputMode="email" autoComplete="email" autoCapitalize="off" spellCheck={false}
          placeholder="you@brand.com"
        />
        <Field
          form={form} name="phone" label="Phone" required
          type="tel" inputMode="tel" autoComplete="tel" placeholder="+91 98765 43210"
        />
        <Field
          form={form} name="website" label="Company website" optional
          type="url" inputMode="url" autoComplete="url" autoCapitalize="off" spellCheck={false}
          placeholder="yourbrand.com"
        />
      </div>

      <Choices
        form={form} name="partnership" label="What kind of partnership?" required multiple
        options={PARTNERSHIPS} hint="Choose all that apply."
      />

      <Field
        form={form} name="cities" label="Which city or cities?" required
        placeholder="e.g. Delhi NCR, Mumbai — or anywhere"
      />

      <Field
        form={form} name="budget" label="Budget range" optional as="select" options={BUDGET_RANGES}
        hint="Optional. Leave it blank, or choose “Prefer to discuss”."
      />

      <Field
        form={form} name="message" label="Message" required as="textarea"
        maxLength={2000} placeholder="What you'd like to achieve, timings, anything we should know"
      />

      <SendError form={form} />
      <Submit form={form}>Send to the partnerships team</Submit>
    </SiteForm>
  );
}
